import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
  reload,
  onAuthStateChanged,
  type AuthProvider,
  User as FirebaseUser
} from 'firebase/auth';

import { auth, googleProvider, facebookProvider, githubProvider } from '../config/firebase';
import {
  isPopupAuthFailure,
  markSocialAuthRedirectFailed,
  markSocialAuthRedirectReady,
  readSocialAuthPending,
  shouldUseSocialAuthRedirect,
  SocialAuthRedirectInitiatedError,
  type SocialAuthProvider,
} from '../utils/socialAuthRedirect';
import { User, mergeClientRoles, type ManualInviteAccountFor } from '@court-sync/shared';
import { API_BASE_URL } from '../config/api';
import { retryWithBackoff } from '../utils';
import { getImpersonationRequestHeaders } from '../utils/impersonationHeaders';
import { clearStaleImpersonationIfNeeded } from '../utils/impersonationRecovery';

export interface AuthError {
  code: string;
  message: string;
}

export type AcademySignUpResult = {
  user: User;
  verificationEmailFailed?: boolean;
};

export class AuthService {
  // Email/Password Authentication
  static async signInWithEmail(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Get user profile from backend API
      const idToken = await userCredential.user.getIdToken();
      return await this.getUserProfileViaAPI(idToken);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  static async signUpWithEmail(
    email: string,
    password: string,
    displayName: string,
    role?: 'guardian' | 'academy_owner' | 'coach',
    academyName?: string,
    returnUrl?: string,
    legalAcceptance?: {
      termsAccepted: boolean;
      privacyAccepted: boolean;
      termsVersion: string;
      privacyVersion: string;
      marketingEmailConsent?: boolean;
    },
    country?: string,
    stateOrProvince?: string
  ): Promise<{ user: User; needsVerification: boolean; verificationEmailFailed?: boolean }> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Persist platform user + legal acceptance before updateProfile / verification / other work.
      // Otherwise GET /user/profile (from onAuthStateChanged) can auto-create a stub row without
      // terms_* / privacy_* while the ID token still lacks display name — and a concurrent
      // social-login insert can hit duplicate uid without merging payload (see handleNewUser 23505 path).
      const idToken = await userCredential.user.getIdToken();
      const nameParts = (displayName || '').trim().split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || 'User';
      const lastName = nameParts.slice(1).join(' ') || '';

      const user = await this.createOrUpdateUserViaAPI(
        userCredential.user,
        idToken,
        role,
        legalAcceptance,
        undefined,
        { firstName, lastName }
      );

      await updateProfile(userCredential.user, { displayName });
      try {
        await reload(userCredential.user);
      } catch {
        // Non-fatal: token may still refresh on next getIdToken()
      }

      const verificationReturnUrl = returnUrl
        ? `${window.location.origin}${returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`}`
        : `${window.location.origin}/email-verification?email=${encodeURIComponent(email)}`;
      let verificationEmailFailed = false;
      try {
        await AuthService.resendEmailVerification(email, {
          returnUrl: verificationReturnUrl,
          firstName,
        });
      } catch (emailError) {
        verificationEmailFailed = true;
        console.warn('Verification email failed after signup; account was created:', emailError);
      }

      let academyId: string | undefined;

      if (academyName && role === 'academy_owner') {
        try {
          await new Promise(resolve => setTimeout(resolve, 100));
          academyId = await this.createAcademyForUser(academyName, country, stateOrProvince);
        } catch (academyError) {
          console.error('Academy creation failed:', academyError);
          console.warn('Continuing registration without academy creation');
        }
      }

      // If academy was created, update user document with academyId
      if (academyId) {
        // Academy ID will be set when academy is created via the backend API
        // The user document should already have it, but we can verify
        if (!user.academyId) {
          // Update user profile with academyId via backend API
          try {
            const { userService } = await import('./user');
            await userService.updateProfile({
              firstName,
              lastName,
              email: userCredential.user.email || '',
            });
          } catch (updateError) {
            console.warn('Failed to update user with academyId:', updateError);
          }
        }
      }

      return { user, needsVerification: true, verificationEmailFailed };
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  // Academy member registration (booking, standalone register, private booking).
  // Default role is student; guardian is added when booking for a child (backend bookingPublic).
  static async signUpAsGuardian(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    returnUrl?: string,
    legalAcceptance?: {
      termsAccepted: boolean;
      privacyAccepted: boolean;
      termsVersion: string;
      privacyVersion: string;
      marketingEmailConsent?: boolean;
    },
    academyId?: string,
    role: 'guardian' | 'student' = 'student',
    profileContext?: {
      phone?: string;
      manualInviteToken?: string;
      manualInviteAccountFor?: ManualInviteAccountFor;
    }
  ): Promise<AcademySignUpResult> {
    try {
      const displayName = `${firstName} ${lastName}`;
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      const idToken = await userCredential.user.getIdToken();
      const user = await this.createOrUpdateUserViaAPI(
        userCredential.user,
        idToken,
        role,
        legalAcceptance,
        academyId,
        { firstName, lastName },
        profileContext
      );

      await updateProfile(userCredential.user, { displayName });
      try {
        await reload(userCredential.user);
      } catch {
        // Non-fatal
      }

      const verificationReturnUrl = returnUrl
        ? `${window.location.origin}${returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`}`
        : undefined;
      let verificationEmailFailed = false;
      try {
        await AuthService.resendEmailVerification(email, {
          returnUrl: verificationReturnUrl,
          firstName,
        });
      } catch (emailError) {
        verificationEmailFailed = true;
        console.warn('Verification email failed after signup; account was created:', emailError);
      }

      return { user, verificationEmailFailed };
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Request a password reset email via backend (Mailgun + Firebase action link).
   */
  static async requestPasswordResetEmail(email: string, returnUrl?: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        ...(returnUrl && { returnUrl }),
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send password reset email');
    }
  }

  /** @deprecated Use requestPasswordResetEmail — client Firebase emails replaced by Mailgun */
  static async sendPasswordResetEmail(email: string, continueUrl: string): Promise<void> {
    return AuthService.requestPasswordResetEmail(email, continueUrl);
  }

  // Social Authentication
  static async signInWithGoogle(role?: 'guardian' | 'academy_owner' | 'coach'): Promise<User> {
    return this.signInWithSocialProvider(googleProvider, 'google', role);
  }

  static async signInWithFacebook(role?: 'guardian' | 'academy_owner' | 'coach'): Promise<User> {
    return this.signInWithSocialProvider(facebookProvider, 'facebook', role);
  }

  static async signInWithGithub(role?: 'guardian' | 'academy_owner' | 'coach'): Promise<User> {
    return this.signInWithSocialProvider(githubProvider, 'github', role);
  }

  /**
   * Completes a social sign-in that used full-page redirect (see signInWithSocialProvider).
   * Must run once on app load before relying on onAuthStateChanged for redirect returns.
   */
  static async completeSocialAuthRedirectIfNeeded(): Promise<void> {
    try {
      const result = await getRedirectResult(auth);
      if (!result?.user) return;

      const pending = readSocialAuthPending();
      const role = pending?.role;
      const idToken = await result.user.getIdToken();
      await this.createOrUpdateUserViaAPI(result.user, idToken, role);
      markSocialAuthRedirectReady();
    } catch (error: unknown) {
      const authError = this.handleAuthError(error);
      markSocialAuthRedirectFailed(authError.message);
      throw authError;
    }
  }

  private static async signInWithSocialProvider(
    provider: AuthProvider,
    _providerKey: SocialAuthProvider,
    role?: 'guardian' | 'academy_owner' | 'coach'
  ): Promise<User> {
    const pending = readSocialAuthPending();
    const effectiveRole = role ?? pending?.role;

    try {
      if (shouldUseSocialAuthRedirect()) {
        await signInWithRedirect(auth, provider);
        throw new SocialAuthRedirectInitiatedError();
      }

      const userCredential = await signInWithPopup(auth, provider);
      const idToken = await userCredential.user.getIdToken();
      await this.createOrUpdateUserViaAPI(userCredential.user, idToken, effectiveRole);
      return await this.getUserProfileViaAPI(idToken);
    } catch (error: unknown) {
      if (error instanceof SocialAuthRedirectInitiatedError) {
        throw error;
      }

      if (isPopupAuthFailure(error)) {
        await signInWithRedirect(auth, provider);
        throw new SocialAuthRedirectInitiatedError();
      }

      throw this.handleAuthError(error);
    }
  }

  // Sign Out
  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  // Send or resend email verification via backend (Mailgun + Firebase action link)
  static async resendEmailVerification(
    email: string,
    options?: { returnUrl?: string; firstName?: string }
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ...(options?.returnUrl && { returnUrl: options.returnUrl }),
          ...(options?.firstName && { firstName: options.firstName }),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to resend verification email');
    }
  }

  // Update user role
  static async updateUserRole(userId: string, role: 'guardian' | 'academy_owner' | 'coach'): Promise<void> {
    try {
      const token = await this.getCurrentUserToken();
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/user/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user role');
      }

      // Role is updated via backend API, no need to update local Firestore document
      console.log('User role updated successfully via API');
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  // Get current Firebase user token
  static async getCurrentUserToken(forceRefresh = false): Promise<string | null> {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        return await currentUser.getIdToken(forceRefresh);
      }
      return null;
    } catch (error: any) {
      console.error('Error getting user token:', error);
      return null;
    }
  }

  // Auth State Observer
  static onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user profile from backend API with retry for race conditions
          // (user document may not be created yet during social login flow)
          const idToken = await firebaseUser.getIdToken();

          try {
            // Retry fetching user profile with exponential backoff
            // This handles the race condition where onAuthStateChanged fires
            // before the user document is created in Firestore
            const user = await retryWithBackoff(
              () => this.getUserProfileViaAPI(idToken),
              {
                maxRetries: 3,
                initialDelayMs: 300,
                maxDelayMs: 2000,
                // Only retry on "not found" errors (race condition)
                retryCondition: (error: unknown) => {
                  const message = (error as Error)?.message ?? '';
                  return message.includes('not found') || message.includes('404');
                },
              }
            );
            callback(user);
          } catch (apiError: any) {
            // After retries exhausted, if still not found, create the user
            if (apiError.message?.includes('not found') || apiError.message?.includes('404')) {
              console.log('User document not found after retries, creating via API');
              try {
                // Do not pass a default role - backend creates user without assigning any role.
                // Roles are assigned when users take specific actions (academy, coach invite, booking).
                const user = await this.createOrUpdateUserViaAPI(firebaseUser, idToken, undefined);
                callback(user);
              } catch (createError) {
                console.error('Error creating user via API:', createError);
                callback(null);
              }
            } else {
              console.error('Error getting user profile:', apiError);
              callback(null);
            }
          }
        } catch (error) {
          console.error('Error in onAuthStateChanged:', error);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }

  // Get current user
  static getCurrentUser(): User | null {
    if (!auth.currentUser) return null;
    // This is a simplified version - in practice you'd want to fetch from Firestore
    return this.firebaseUserToAppUser(auth.currentUser);
  }

  // Refresh user profile from backend
  static async refreshUserProfile(options?: { forceTokenRefresh?: boolean }): Promise<User | null> {
    try {
      const token = await this.getCurrentUserToken(options?.forceTokenRefresh ?? false);
      if (!token) return null;

      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getImpersonationRequestHeaders(),
        },
      });

      let effectiveResponse = response;
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const errorMessage =
          errorBody && typeof errorBody === 'object' && 'error' in errorBody
            ? String(errorBody.error)
            : undefined;
        if (response.status === 403 && clearStaleImpersonationIfNeeded(errorMessage)) {
          effectiveResponse = await fetch(`${API_BASE_URL}/user/profile`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        } else if (!effectiveResponse.ok) {
          throw new Error(errorMessage || 'Failed to fetch user profile');
        }
      }

      if (!effectiveResponse.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await effectiveResponse.json();
      const userData = data.data;
      if (!userData) return null;

      const rolesWithAcademy = userData.rolesWithAcademy ?? [];
      const roles = mergeClientRoles(
        Array.isArray(userData.roles) ? userData.roles : undefined,
        rolesWithAcademy
      );

      return {
        id: userData.id,
        uid: userData.uid ?? userData.id,
        email: userData.email,
        firstName: userData.firstName || 'User',
        lastName: userData.lastName || '',
        academyId: userData.academyId,
        roles,
        rolesWithAcademy: rolesWithAcademy.length > 0 ? rolesWithAcademy : undefined,
        hasAcademyProfile: userData.hasAcademyProfile ?? false,
        isActive: true,
        isEmailVerified: userData.isEmailVerified ?? false,
        phone: userData.phone,
        preferences: userData.preferences || { theme: 'system', language: 'en' },
        avatarUrl: userData.avatarUrl,
        createdAt: userData.createdAt ? new Date(userData.createdAt) : new Date(),
        updatedAt: userData.updatedAt ? new Date(userData.updatedAt) : new Date(),
      };
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      return null;
    }
  }

  // Helper Methods
  private static async getUserProfileViaAPI(
    idToken: string,
    additionalData?: Partial<User>
  ): Promise<User> {
    try {
      let response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          ...getImpersonationRequestHeaders(),
        },
      });

      let data = await response.json();

      if (!response.ok) {
        const errorMessage = typeof data?.error === 'string' ? data.error : undefined;
        if (response.status === 403 && clearStaleImpersonationIfNeeded(errorMessage)) {
          response = await fetch(`${API_BASE_URL}/user/profile`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          });
          data = await response.json();
        }
      }

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found');
        }
        throw new Error(data.error || 'Failed to get user profile');
      }

      if (!data.success || !data.data) {
        throw new Error('Invalid response from server');
      }

      // Merge additional data if provided
      const userData = data.data;
      const rolesWithAcademy = userData.rolesWithAcademy ?? [];
      const roles = mergeClientRoles(
        Array.isArray(userData.roles) ? userData.roles : undefined,
        rolesWithAcademy
      );
      const user: User = {
        id: userData.id,
        uid: userData.uid ?? userData.id,
        email: userData.email,
        firstName: additionalData?.firstName || userData.firstName || 'User',
        lastName: additionalData?.lastName || userData.lastName || '',
        academyId: userData.academyId,
        roles,
        rolesWithAcademy: rolesWithAcademy.length > 0 ? rolesWithAcademy : undefined,
        hasAcademyProfile: userData.hasAcademyProfile ?? false,
        isActive: userData.isActive ?? true,
        isEmailVerified: userData.isEmailVerified ?? false,
        phone: userData.phone,
        preferences: userData.preferences || {
          theme: 'system',
          language: 'en',
          notifications: {
            email: true,
            push: true,
            sms: false,
          },
        },
        avatarUrl: userData.avatarUrl ?? additionalData?.avatarUrl,
        createdAt: userData.createdAt?.toDate ? new Date(userData.createdAt.toDate()) : new Date(userData.createdAt || Date.now()),
        updatedAt: userData.updatedAt?.toDate ? new Date(userData.updatedAt.toDate()) : new Date(userData.updatedAt || Date.now()),
        ...additionalData,
      };

      return user;
    } catch (error: any) {
      console.error('Error getting user profile via API:', error);
      throw error;
    }
  }

  private static async createOrUpdateUserViaAPI(
    firebaseUser: FirebaseUser,
    idToken: string,
    role?: 'guardian' | 'academy_owner' | 'coach' | 'student',
    legalAcceptance?: {
      termsAccepted: boolean;
      privacyAccepted: boolean;
      termsVersion: string;
      privacyVersion: string;
      marketingEmailConsent?: boolean;
    },
    academyId?: string,
    explicitName?: { firstName: string; lastName: string },
    profileContext?: {
      phone?: string;
      manualInviteToken?: string;
      manualInviteAccountFor?: ManualInviteAccountFor;
    }
  ): Promise<User> {
    try {
      // Use explicit names when provided (e.g. from signUpAsGuardian); otherwise parse from displayName
      let firstName: string;
      let lastName: string;
      const displayName = (firebaseUser.displayName || '').trim();
      const displayParts = displayName ? displayName.split(/\s+/).filter(Boolean) : [];
      if (explicitName?.firstName?.trim() || explicitName?.lastName?.trim()) {
        firstName =
          explicitName?.firstName?.trim() ||
          displayParts[0] ||
          'User';
        lastName =
          (explicitName?.lastName || '').trim() ||
          displayParts.slice(1).join(' ') ||
          '';
      } else {
        firstName = displayParts[0] || 'User';
        lastName = displayParts.slice(1).join(' ') || '';
      }

      const requestBody = {
        idToken,
        role, // Only pass explicit role; no default - backend assigns roles only for specific flows
        ...(academyId && { academyId }),
        firstName,
        lastName,
        ...(profileContext?.phone?.trim() && { phone: profileContext.phone.trim() }),
        ...(profileContext?.manualInviteToken?.trim() && {
          manualInviteToken: profileContext.manualInviteToken.trim(),
        }),
        ...(profileContext?.manualInviteAccountFor && {
          manualInviteAccountFor: profileContext.manualInviteAccountFor,
        }),
        ...(legalAcceptance && {
          termsAccepted: legalAcceptance.termsAccepted,
          privacyAccepted: legalAcceptance.privacyAccepted,
          marketingEmailConsent: legalAcceptance.marketingEmailConsent,
        }),
      };


      const response = await fetch(`${API_BASE_URL}/auth/social-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create/update user document');
      }

      if (!data.success || !data.data?.user) {
        throw new Error('Invalid response from server');
      }

      return data.data.user;
    } catch (error: any) {
      console.error('Error creating/updating user via API:', error);
      throw new Error(error.message || 'Failed to create/update user document');
    }
  }

  static async createOrganization(
    input: import('@court-sync/shared').CreateOrganizationInput
  ): Promise<{
    organizationId: string;
    academyId?: string;
    clubId?: string;
    slug: string;
    name: string;
  }> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/create-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create organization');
      }

      if (data.success && data.data?.organizationId) {
        return data.data;
      }

      throw new Error('Failed to create organization');
    } catch (error: any) {
      console.error('Error creating organization:', error);
      throw new Error(error.message || 'Failed to create organization');
    }
  }

  static async createAcademyForUser(
    academyName: string,
    country?: string,
    stateOrProvince?: string,
    operatingProfile?: import('@court-sync/shared').AcademyOperatingProfileInput,
    timezone?: string
  ): Promise<string> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const body: Record<string, unknown> = { academyName };
      if (country?.trim()) body.country = country.trim().toUpperCase();
      if (stateOrProvince?.trim()) body.stateOrProvince = stateOrProvince.trim();
      if (operatingProfile) body.operatingProfile = operatingProfile;
      if (timezone?.trim()) body.timezone = timezone.trim();

      const response = await fetch(`${API_BASE_URL}/user/create-academy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create academy');
      }

      if (data.success && data.data?.academyId) {
        return data.data.academyId;
      } else {
        throw new Error('Failed to create academy');
      }
    } catch (error: any) {
      console.error('Error creating academy:', error);
      throw new Error(error.message || 'Failed to create academy');
    }
  }


  /**
   * Synchronous placeholder — `id` is set to Firebase UID, NOT the platform UUID.
   * Callers needing the real platform user ID must use `refreshUserProfile()` instead.
   */
  private static firebaseUserToAppUser(firebaseUser: FirebaseUser): User {
    return {
      id: firebaseUser.uid, // TEMP: Firebase UID; real platform UUID comes from refreshUserProfile()
      uid: firebaseUser.uid,
      email: firebaseUser.email!,
      firstName: firebaseUser.displayName?.split(' ')[0] || 'User',
      lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
      roles: ['guardian'],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Create academy for social login user
  static async createAcademyForSocialUser(
    academyName: string,
    legalAcceptance?: {
      termsAccepted: boolean;
      privacyAccepted: boolean;
      termsVersion: string;
      privacyVersion: string;
      marketingEmailConsent?: boolean;
    },
    country?: string,
    stateOrProvince?: string,
    operatingProfile?: import('@court-sync/shared').AcademyOperatingProfileInput,
    timezone?: string
  ): Promise<{ academyId: string; academyName: string; slug: string }> {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('User not authenticated');
      }

      const requestBody: Record<string, unknown> = { academyName };
      if (legalAcceptance) {
        requestBody.termsAccepted = legalAcceptance.termsAccepted;
        requestBody.privacyAccepted = legalAcceptance.privacyAccepted;
        requestBody.termsVersion = legalAcceptance.termsVersion;
        requestBody.privacyVersion = legalAcceptance.privacyVersion;
        if (legalAcceptance.marketingEmailConsent !== undefined) {
          requestBody.marketingEmailConsent = legalAcceptance.marketingEmailConsent;
        }
      }
      if (country?.trim()) requestBody.country = country.trim().toUpperCase();
      if (stateOrProvince?.trim()) requestBody.stateOrProvince = stateOrProvince.trim();
      if (operatingProfile) requestBody.operatingProfile = operatingProfile;
      if (timezone?.trim()) requestBody.timezone = timezone.trim();

      const response = await fetch(`${API_BASE_URL}/user/create-academy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create academy');
      }

      return data.data;
    } catch (error: any) {
      console.error('Error creating academy for social user:', error);
      throw new Error(error.message || 'Failed to create academy');
    }
  }

  private static handleAuthError(error: any): AuthError {
    let message = 'An error occurred during authentication';

    switch (error.code) {
      case 'auth/user-not-found':
        message = 'No account found with this email address';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password';
        break;
      case 'auth/invalid-credential':
        message = 'Invalid email or password';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address';
        break;
      case 'auth/popup-closed-by-user':
        message = 'Sign-in popup was closed before completing';
        break;
      case 'auth/popup-blocked':
        message = 'Sign-in popup was blocked by the browser';
        break;
      case 'auth/cancelled-popup-request':
        message = 'Sign-in was cancelled';
        break;
      case 'auth/network-request-failed':
        message = 'Network error - please check your connection';
        break;
      default:
        message = error.message || message;
    }

    return {
      code: error.code || 'unknown',
      message
    };
  }
}

